import LabeledSelect from 'components/LabeledSelect';
import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { useEffect, useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { globalActionDeviceDataMaps } from '../../utils/deviceFieldMetadata.utils';
import { buildDeviceValueMap, getDeviceTypeString } from '../../utils/builder.utils';
import DeviceCustomInput from './DeviceCustomInput';
import DeviceLatestStatus from './DeviceLatestStatus';

const resolveControllerDevice = (group, connectedDevices, preferredDeviceType = '') => {
  if (!group?.controllers?.length) {
    return null;
  }

  const controllerDevices = group.controllers
    .map((controllerId) => connectedDevices.find((device) => device.id === controllerId))
    .filter(Boolean);

  if (!controllerDevices.length) {
    return null;
  }

  if (preferredDeviceType) {
    const matchingController = controllerDevices.find(
      (device) => getDeviceTypeString(device) === preferredDeviceType,
    );
    if (matchingController) {
      return matchingController;
    }
  }

  return controllerDevices[0];
};

const DeviceGroup = ({ setData, data, setError, readOnly = false }) => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const { data: deviceGroups = [] } = useGetGroupsQuery();
  const [deviceGroupsList, setDeviceGroupsList] = useState([]);
  const [group, setGroup] = useState();

  useEffect(() => {
    if (deviceGroups) {
      const controllerGroups = deviceGroups.filter((gp) => gp.controllers?.length > 0);
      setDeviceGroupsList(controllerGroups);
    }
  }, [deviceGroups]);

  useEffect(() => {
    if (
      data?.group === undefined ||
      data?.keyToCheck === undefined ||
      data?.keyToCompare === undefined ||
      data?.operator === undefined ||
      data?.valueToCompare === undefined ||
      data?.selectedDevice === undefined ||
      data?.inputValueType === undefined
    ) {
      setData({
        group: null,
        keyToCheck: '',
        keyToCompare: '',
        operator: '',
        valueToCompare: '',
        selectedDevice: '',
        inputValueType: false,
        error: true,
      });
    }

    if (data?.group?.id) {
      const foundGroup = deviceGroupsList.find((g) => g.id === data.group.id);
      setGroup(foundGroup ?? null);

      if (foundGroup && !data?.selectedDevice) {
        const controllerDevice = resolveControllerDevice(
          foundGroup,
          connectedDevices,
          data?.deviceType,
        );

        if (controllerDevice) {
          setData({
            ...data,
            group: foundGroup,
            selectedDevice: {
              ...controllerDevice,
              data: controllerDevice.data?.length ? [controllerDevice.data[0]] : [],
            },
            deviceType: getDeviceTypeString(controllerDevice),
          });
        }
      }
    }
  }, [connectedDevices, data, deviceGroupsList, setData]);

  const updateData = (item) => {
    let nextItem = item;
    if (item.selectedDevice?.data?.length) {
      nextItem = {
        ...item,
        selectedDevice: {
          ...item.selectedDevice,
          data: [item.selectedDevice.data[0]],
        },
      };
    }
    const nextData = { ...data, ...nextItem };
    setData({
      ...nextData,
      error: verifyData(nextData),
    });
  };

  const verifyData = (obj) => {
    let error = false;
    if (
      obj.group === null ||
      obj.deviceType === '' ||
      obj.selectedDevice === '' ||
      obj.selectedDevice === null ||
      obj.keyToCheck === '' ||
      (obj.keyToCompare === '' && obj.valueToCompare === '') ||
      (obj.keyToCompare === null && obj.valueToCompare === null) ||
      obj.operator === '' ||
      obj.operator === null
    ) {
      error = true;
      setError({ message: 'Action incomplete, check for missing fields.' });
    }
    if (!error) setError(null);
    return error;
  };

  const operators = [
    { operator: '==', types: ['all'] },
    { operator: '!=', types: ['all'] },
    { operator: '>', types: ['float'] },
    { operator: '<', types: ['float'] },
    { operator: '>=', types: ['float'] },
    { operator: '<=', types: ['float'] },
  ];

  const groupLocked =
    readOnly && !!data?.group?.id && !deviceGroupsList.some((g) => g.id === data?.group?.id);

  const valueMap = data.selectedDevice
    ? buildDeviceValueMap(data.selectedDevice, globalActionDeviceDataMaps) || {}
    : {};

  const keyToCheckOptions = Object.keys(valueMap).map((key) => ({
    value: key,
    label: valueMap[key].name,
  }));

  const typeOfKey = valueMap[data.keyToCheck]?.type;
  const operatorOptions = operators
    .filter((op) => op.types.includes('all') || op.types.includes(typeOfKey))
    .map((op) => ({ value: op.operator, label: op.operator === '==' ? '=' : op.operator }));

  const keyToCompareUnits = valueMap[data.keyToCheck]?.units;
  const keyToCompareOptions = Object.keys(valueMap)
    .filter((key) => valueMap[key] && valueMap[key].units === keyToCompareUnits)
    .map((key) => ({ value: key, label: valueMap[key].name }));

  return (
    <div className="d-flex flex-column w-100">
      <div className="global-action-builder-row d-flex flex-column flex-md-row p-3 rounded-2 m-2 gap-3 align-items-stretch align-items-md-center">
        {groupLocked ? (
          <LabeledSelect label="Device Group:" locked className="flex-fill" />
        ) : deviceGroupsList.length > 0 ? (
          <LabeledSelect
            label="Device Group:"
            disabled={readOnly}
            value={group?.name || ''}
            onChange={(selectedName) => {
              const foundGroup = deviceGroupsList.find((g) => g.name === selectedName);
              const controllerDevice = resolveControllerDevice(foundGroup, connectedDevices);

              setGroup(foundGroup || null);

              updateData({
                selectedDevice: controllerDevice
                  ? {
                      ...controllerDevice,
                      data: controllerDevice.data?.length ? [controllerDevice.data[0]] : [],
                    }
                  : null,
                deviceType: getDeviceTypeString(controllerDevice),
                group: foundGroup || null,
                keyToCheck: '',
                operator: '',
                keyToCompare: '',
                valueToCompare: '',
              });
            }}
            options={deviceGroupsList.map((g) => ({ value: g.name, label: g.name }))}
            className="flex-fill"
          />
        ) : (
          <div>No Device Groups with Controllers available.</div>
        )}
        {group && (
          <div className="d-flex align-items-center">
            <Badge bg="light" text="dark" className="px-3 py-2 rounded-2">
              {group?.devices?.length ?? 0} Devices
            </Badge>
          </div>
        )}
      </div>

      {data.selectedDevice && (
        <DeviceLatestStatus device={data.selectedDevice} dataMaps={globalActionDeviceDataMaps} />
      )}

      {data.selectedDevice && (
        <div className="global-action-builder-row d-flex p-3 rounded-2 m-2 mt-0 w-auto h-auto flex-column flex-md-row gap-3 align-items-stretch align-items-md-center">
          <div className="fw-medium align-self-start">Value:</div>

          <BaseSelect
            wrapperClassName="w-100 w-md-50"
            isSearchable
            classNames={{ control: () => 'global-action-builder-field' }}
            selectProps={{
              options: keyToCheckOptions,
              value: findSelectOption(keyToCheckOptions, data.keyToCheck),
              placeholder: '-Select-',
              isDisabled: readOnly,
              onChange: (option) => updateData({ keyToCheck: option?.value ?? '' }),
            }}
          />

          {data.keyToCheck && (
            <BaseSelect
              wrapperClassName="flex-shrink-0"
              styles={{ container: (base) => ({ ...base, maxWidth: 75 }) }}
              classNames={{ control: () => 'global-action-builder-field' }}
              selectProps={{
                options: operatorOptions,
                value: findSelectOption(operatorOptions, data.operator),
                placeholder: 'Select',
                isDisabled: readOnly,
                onChange: (option) => updateData({ operator: option?.value ?? '' }),
              }}
            />
          )}

          {data.operator &&
            (data.inputValueType ? (
              <div className="flex-fill w-25">
                <DeviceCustomInput
                  value={data.valueToCompare}
                  readOnly={readOnly}
                  onChange={(value) => updateData({ valueToCompare: value })}
                  dataMapItem={
                    buildDeviceValueMap(data.selectedDevice, globalActionDeviceDataMaps)[
                      data.keyToCheck
                    ]
                  }
                />
              </div>
            ) : (
              <BaseSelect
                wrapperClassName="w-100 w-md-50"
                isSearchable
                classNames={{ control: () => 'global-action-builder-field' }}
                selectProps={{
                  options: keyToCompareOptions,
                  value: findSelectOption(keyToCompareOptions, data.keyToCompare),
                  placeholder: '-Select-',
                  isDisabled: readOnly,
                  onChange: (option) => updateData({ keyToCompare: option?.value ?? '' }),
                }}
              />
            ))}

          {data.operator && (
            <Button
              variant="light"
              size="sm"
              className="rounded-2 text-nowrap w-auto"
              style={{ minWidth: 165 }}
              onClick={() =>
                updateData({
                  valueToCompare: '',
                  keyToCompare: '',
                  inputValueType: !data.inputValueType,
                })
              }
            >
              {data.inputValueType ? 'Change to compare' : 'Change to custom value'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceGroup;
